import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, inject, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { nameValidator, noWhitespaceValidator, phoneValidator, requiredEmailValidator } from '@k12/validators';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { Subject } from 'rxjs';
import { SchoolContact, SeverityLevel, Validation, SvgIcon, ToastMessageType } from '@k12/models';
import { LoadingSpinnerService, ToastMessageService, SchoolsService } from '@k12/app-services';
import { AppInsightsService } from '@k12/error_handler';
import { FormInlineErrorComponent } from '@k12/forms';
import { ContactStatus } from '../contact-status-data';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'lib-edit-contact',
  providers: [provideNgxMask()],
  imports: [
    CommonModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIcon,
    MatInputModule,
    MatRadioModule,
    MatSelectModule,
    NgxMaskDirective,
    ReactiveFormsModule,
    FormInlineErrorComponent
  ],
  templateUrl: './new-edit-contact.component.html',
  styleUrls: ['./new-edit-contact.component.scss']
})
export class NewEditContact implements OnInit, OnDestroy {
  @Output() contactUpdated = new EventEmitter<void>();

  contactDialogData = inject(MAT_DIALOG_DATA) as {
    data?: SchoolContact;
    isAdminView?: boolean;
    programPartnerId?: string;
    institutionId?: string;
  };
  editContactForm!: FormGroup;
  SvgIcon = SvgIcon;
  Validation = Validation;

  private readonly appInsightsService = inject(AppInsightsService);
  private readonly dialogRef: MatDialogRef<NewEditContact> = inject(MatDialogRef<NewEditContact>);
  private readonly formbuilder = inject(FormBuilder);
  private readonly loadingSpinnerService = inject(LoadingSpinnerService);
  private readonly ngUnsubscribe: Subject<void> = new Subject();
  private readonly schoolsService = inject(SchoolsService);
  private readonly toastMessageService = inject(ToastMessageService);

  ngOnInit(): void {
    this.editContactForm = this.formbuilder.group({
      firstName: ['', [Validators.required, nameValidator()]],
      lastName: ['', [Validators.required, nameValidator()]],
      jobTitle: ['', [Validators.required, noWhitespaceValidator()]],
      email: ['', [Validators.required, requiredEmailValidator()]],
      phone: ['', phoneValidator()],
      extNumber: ['', Validators.maxLength(4)],
      primaryContact: [false],
      isHDMA: ['No']
    });

    if (this.contactDialogData?.data) {
      const rawPhone = this.contactDialogData.data.person?.personPhones?.[0]?.phone?.phoneNumber || '';
      this.editContactForm.patchValue({
        firstName: this.contactDialogData.data.person?.firstName || '',
        lastName: this.contactDialogData.data.person?.lastName || '',
        jobTitle: this.contactDialogData.data.person?.title || '',
        email: this.contactDialogData.data.person?.personEmails?.[0]?.emailAddress || '',
        phone: this.formatPhoneNumber(rawPhone),
        extNumber:
          this.contactDialogData.data.person?.personPhones?.[0]?.phone?.extension ||
          this.contactDialogData.data.person?.personPhones?.[0]?.extension ||
          '',
        isHDMA: this.contactDialogData.data.isHDMA ? 'Yes' : 'No',
        primaryContact: this.isPrimaryContact
      });

      if (!this.contactDialogData.isAdminView) {
        this.editContactForm.get('firstName')?.disable();
        this.editContactForm.get('lastName')?.disable();
      }

      this.editContactForm.get('email')?.disable();

      if (this.contactDialogData.data.isPrimaryContact) {
        this.editContactForm.get('primaryContact')?.disable();
      }
    }

    this.editContactForm.get('isHDMA')?.disable();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  onCancelEditContact(): void {
    this.dialogRef.close();
  }

  onSaveEditContact(): void {
    if (this.contactDialogData?.data) {
      this.handleUpdateContact();
    } else {
      this.handleInviteContact();
    }
  }

  get isPrimaryContact(): boolean {
    return this.contactDialogData?.data ? this.contactDialogData.data.isPrimaryContact : false;
  }

  protected onExtensionInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    input.value = input.value.replaceAll(/\D/g, '').slice(0, 4);
    this.editContactForm.get('extNumber')?.setValue(input.value, { emitEvent: false });
  }

  private formatPhoneNumber(phoneNumber: string): string {
    if (!phoneNumber) {
      return '';
    }
    // Extract only digits
    const digits = phoneNumber.replace(/\D/g, '');

    // If we have exactly 10 digits, format as (XXX) XXX-XXXX
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    }

    // Return empty string if invalid length (phone is optional)
    return '';
  }

  protected handleUpdateContact(): void {
    const formData = this.editContactForm.value;
    try {
      if (formData === null) {
        this.appInsightsService.logEvent(`Schools client error`, {
          message: 'Update Contact form is empty.'
        });
        this.loadingSpinnerService.hideSpinner();
        return;
      }
      // create schoolContact from the form submission.
      const updateContact = {
        programPartnerId: this.contactDialogData?.data?.programPartnerId,
        institutionId: this.contactDialogData?.data?.institutionId,
        statusId: this.contactDialogData?.data?.isHDMA ? ContactStatus.Active : this.contactDialogData?.data?.statusId,
        isHDMA: this.contactDialogData?.data?.isHDMA ?? false,
        isPrimaryContact: formData.primaryContact ?? this.contactDialogData?.data?.isPrimaryContact,
        personId: this.contactDialogData?.data?.personId,
        person: {
          personId: this.contactDialogData?.data?.personId,
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          title: formData.jobTitle.trim(),
          personEmails: [
            {
              emailAddress: this.contactDialogData?.data?.person?.personEmails?.[0]?.emailAddress || '',
              isPrimary: true
            }
          ],
          personPhones: [
            {
              phone: { phoneNumber: formData.phone, extension: formData.extNumber, phoneTypeId: 1 },
              isPrimary: true
            }
          ]
        },
        schoolId: this.contactDialogData?.data?.programPartnerId,
        schoolContactTypeId: 2
      } as unknown as SchoolContact;
      this.schoolsService.updateSchoolContact(updateContact).subscribe({
        next: () => {
          this.successResponseHandler('updated', updateContact);
        },
        error: (response) => {
          this.handleError(response);
        }
      });
    } catch (error: unknown) {
      this.logError(error);
    }
  }

  protected handleInviteContact(): void {
    this.loadingSpinnerService.showSpinner();
    const formData = this.editContactForm.value;
    try {
      if (formData !== null) {
        // create schoolContact from the form submission.
        const contact = {
          programPartnerId: this.contactDialogData?.programPartnerId,
          institutionId: this.contactDialogData?.institutionId,
          statusId: 1,
          isHDMA: false,
          isPrimaryContact: formData.primaryContact,
          person: {
            firstName: formData.firstName,
            lastName: formData.lastName,
            title: formData.jobTitle,
            personEmails: [{ emailAddress: formData.email, isPrimary: true }],
            personPhones: [
              {
                phone: { phoneNumber: formData.phone, extension: formData.extNumber, phoneTypeId: 1 },
                isPrimary: true
              }
            ]
          },
          schoolId: this.contactDialogData?.programPartnerId,
          schoolContactTypeId: 2
        } as unknown as SchoolContact;

        this.schoolsService.createSchoolContact(contact).subscribe({
          next: () => {
            this.successResponseHandler('saved', contact);
          },
          error: (response) => {
            this.handleError(response);
          }
        });
      }
    } catch (error: unknown) {
      this.logError(error);
    }
  }

  private successResponseHandler(message: string, contact: SchoolContact): void {
    this.loadingSpinnerService.hideSpinner();
    this.toastMessageService.showToast(ToastMessageType.Success, `Your changes have been ${message} successfully.`);
    this.dialogRef.close(contact);
  }

  private handleError(response: HttpErrorResponse): void {
    this.loadingSpinnerService.hideSpinner();
    if (response.error.validationErrors && response.error.validationErrors.length > 0) {
      response.error.validationErrors.forEach((ve: string) => {
        //trim off everything before the first ":"
        const errorMessage = ve.substring(ve.indexOf(':') + 1);
        this.toastMessageService.showToast(ToastMessageType.Error, errorMessage);
      });
    } else {
      this.toastMessageService.showToast(
        ToastMessageType.Error,
        `Your changes have not been saved, please contact Admin Support`
      );
      this.appInsightsService.log(response, SeverityLevel.error);
    }
  }

  private logError(error: unknown): void {
    this.appInsightsService.logEvent(`Schools client error`, { message: error });
    this.loadingSpinnerService.hideSpinner();
  }
}
